import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import i18n from "i18next";
import {
    Sparkles, ArrowRight, ArrowLeft, Loader2, Check, Target, Users, X,
    Building2, Globe, User as UserIcon, Briefcase, Radar, Upload, Zap, ExternalLink,
    Plug, CheckCircle2, AlertCircle,
} from "lucide-react";

const STYLE_LABELS = {
    formality: ["Casual", "Conversationeel", "Professioneel", "Formeel"],
    energy: ["Rustig", "Bedacht", "Energiek", "Levendig"],
    warmth: ["Direct", "Zakelijk-warm", "Warm", "Hartelijk"],
};

const INTEGRATION_META = [
    { id: "apollo", label: "Apollo.io", icon: Users, color: "#3b82f6" },
    { id: "perplexity", label: "Perplexity Sonar Pro", icon: Radar, color: "#FF5A00" },
    { id: "unipile", label: "Unipile (LinkedIn+Gmail)", icon: Plug, color: "#0a66c2" },
    { id: "surfe", label: "Surfe", icon: Zap, color: "#10b981" },
];

const TOTAL_STEPS = 7;   // 0=welcome, 1=essence, 2=sales tone, 3=recruiter tone, 4=icp, 5=integrations, 6=first action, 7=ready

export default function OnboardingWizard({ onClose }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { refreshWorkspace } = useAuth();
    const [step, setStep] = useState(0);
    const [saving, setSaving] = useState(false);
    const [generating, setGenerating] = useState(null);
    const [previews, setPreviews] = useState({ sales: "", recruiter: "" });
    const [status, setStatus] = useState(null);
    const [finishing, setFinishing] = useState(false);

    const [essence, setEssence] = useState({ values: "", culture: "", voice_description: "" });
    const [sales, setSales] = useState({ formality: 3, energy: 3, warmth: 3, default_greeting: "Hi {first_name}", default_signoff: "", forbidden: "", samples: "" });
    const [recr, setRecr] = useState({ formality: 3, energy: 4, warmth: 4, default_greeting: "Hi {first_name}", default_signoff: "", forbidden: "", samples: "" });
    const [icp, setIcp] = useState({ sector: "", country: "", size: "", role: "" });

    // Load current onboarding status on mount so a returning user can jump ahead.
    useEffect(() => {
        api.get("/onboarding/status").then((r) => {
            setStatus(r.data);
            const dicp = r.data?.default_icp || {};
            setIcp({
                sector: dicp.sector || "", country: dicp.country || "",
                size: dicp.size || "", role: dicp.role || "",
            });
            // Terugkerende gebruiker → spring direct naar de eerste onvoltooide stap
            // (i.p.v. opnieuw het welkomstscherm). Bij 0 voortgang blijft stap 0.
            const done = r.data?.steps || {};
            const ORDER = ["essence", "sales_tone", "recruiter_tone", "icp", "integrations", "first_action"];
            if ((r.data?.steps_done || 0) > 0) {
                const idx = ORDER.findIndex((k) => !done[k]);
                setStep(idx === -1 ? TOTAL_STEPS : idx + 1);
            }
        }).catch(() => {});
    }, []);

    const parseList = (v) => v.split("\n").map((s) => s.trim()).filter(Boolean).slice(0, 8);
    const parseSamples = (v) => v.split(/---+/).map((s) => s.trim()).filter(Boolean).slice(0, 3);

    const saveEssence = () => api.patch("/tone/brand/essence", {
        values: parseList(essence.values), culture: essence.culture, voice_description: essence.voice_description,
    });
    const saveTone = (type, src) => api.patch(`/tone/${type}`, {
        formality: src.formality, energy: src.energy, warmth: src.warmth,
        default_greeting: src.default_greeting, default_signoff: src.default_signoff,
        forbidden_phrases: parseList(src.forbidden), sample_messages: parseSamples(src.samples),
    });
    const saveICP = () => api.patch("/onboarding/icp", icp);
    const markStep = (name) => api.patch("/onboarding/step", { step: name, done: true }).catch(() => {});

    const generate = async (type) => {
        setGenerating(type);
        try {
            const r = await api.post("/tone/preview", { master_type: type, scenario: "first_touch" });
            setPreviews((p) => ({ ...p, [type]: r.data.preview }));
            await refreshWorkspace();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("onboarding.preview_failed")); }
        finally { setGenerating(null); }
    };

    const refreshStatus = async () => {
        try { const r = await api.get("/onboarding/status"); setStatus(r.data); } catch (e) { /* noop */ }
    };

    const next = async () => {
        try {
            if (step === 0) { setStep(1); return; }
            if (step === 1) { setSaving(true); await saveEssence(); await markStep("essence"); setStep(2); return; }
            if (step === 2) { setSaving(true); await saveTone("sales", sales); await markStep("sales_tone"); setStep(3); return; }
            if (step === 3) { setSaving(true); await saveTone("recruiter", recr); await markStep("recruiter_tone"); setStep(4); return; }
            if (step === 4) { setSaving(true); await saveICP(); await refreshStatus(); setStep(5); return; }
            if (step === 5) { setSaving(true); await markStep("integrations"); await refreshStatus(); setStep(6); return; }
        } catch (e) {
            toast.error(i18n.t("companies.save_failed"));
        } finally {
            setSaving(false);
        }
    };

    const doFirstAction = async (action) => {
        setFinishing(true);
        try {
            const r = await api.post("/onboarding/first-action", { action });
            await api.post("/tone/onboarding/complete").catch(() => {});
            await refreshWorkspace();
            toast.success(i18n.t("onboarding.completed"));
            const redirect = r.data?.redirect || "/app/home";
            onClose();
            navigate(redirect);
        } catch (e) {
            toast.error(i18n.t("common.failed"));
        } finally {
            setFinishing(false);
        }
    };

    const jumpTo = (n) => setStep(Math.max(0, Math.min(TOTAL_STEPS, n)));

    return (
        <div className="fixed inset-0 bg-black/85 backdrop-blur-md z-[100] grid place-items-center p-4 overflow-y-auto" data-testid="onboarding-wizard">
            <div className="w-full max-w-4xl rounded-2xl border border-white/10 bg-[var(--jr-surface)] my-8 jr-fadein">
                {/* Header / progress */}
                <div className="px-8 pt-8 pb-5 border-b border-white/8">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Sparkles className="text-[var(--jr-brand)]" size={18}/>
                            <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--jr-text-3)]">
                                {t("onboarding.step")} {Math.max(1, step)} {t("onboarding.of")} {TOTAL_STEPS}
                            </span>
                        </div>
                        {step < TOTAL_STEPS && (
                            <button onClick={onClose} data-testid="onboarding-skip" className="text-xs text-[var(--jr-text-3)] hover:text-[var(--jr-text-2)] inline-flex items-center gap-1">
                                <X size={12}/> {t("common.skip")}
                            </button>
                        )}
                    </div>
                    <div className="mt-5 flex gap-1.5">
                        {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
                            <button key={i} onClick={() => jumpTo(i + 1)} data-testid={`onboarding-progress-${i}`}
                                className="flex-1 h-1 rounded-full cursor-pointer transition-all"
                                style={{ background: i < step ? "var(--jr-brand)" : "rgba(255,255,255,0.08)" }}
                                aria-label={i18n.t("onboarding.goto_step", { step: i + 1 })}/>
                        ))}
                    </div>
                </div>

                {/* Body */}
                <div className="px-8 py-7 min-h-[440px]">
                    {step === 0 && <WelcomeStep t={t}/>}
                    {step === 1 && <EssenceStep t={t} essence={essence} setEssence={setEssence}/>}
                    {step === 2 && <ToneStep title={t("onboarding.step2_title")} subtitle={t("onboarding.step2_subtitle")} accent="var(--jr-brand)" type="sales" value={sales} onChange={setSales}/>}
                    {step === 3 && <ToneStep title={t("onboarding.step3_title")} subtitle={t("onboarding.step3_subtitle")} accent="var(--jr-amber)" type="recruiter" value={recr} onChange={setRecr}/>}
                    {step === 4 && <ICPStep t={t} icp={icp} setIcp={setIcp}/>}
                    {step === 5 && <IntegrationsStep t={t} status={status} onChange={refreshStatus}/>}
                    {step === 6 && <FirstActionStep t={t} onAction={doFirstAction} disabled={finishing} previews={previews} onGenerate={generate} generating={generating}/>}
                    {step === TOTAL_STEPS && <ReadyStep t={t} status={status}/>}
                </div>

                {/* Footer / nav */}
                {step !== 6 && step !== TOTAL_STEPS && (
                    <div className="px-8 py-5 border-t border-white/8 flex items-center justify-between">
                        <div>
                            {step > 0 && (
                                <button onClick={() => setStep(step - 1)} data-testid="onboarding-back" className="inline-flex items-center gap-2 text-sm text-[var(--jr-text-2)] hover:text-white">
                                    <ArrowLeft size={14}/> {t("common.back")}
                                </button>
                            )}
                        </div>
                        <div>
                            <button onClick={next} disabled={saving} data-testid="onboarding-next"
                                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[var(--jr-brand)] text-white font-medium hover:bg-[var(--jr-brand-2)] disabled:opacity-60">
                                {saving ? <><Loader2 className="animate-spin" size={14}/> {t("common.saving")}</> : <>{step === 0 ? t("onboarding.start") : t("common.next")} <ArrowRight size={14}/></>}
                            </button>
                        </div>
                    </div>
                )}
                {step === TOTAL_STEPS && (
                    <div className="px-8 py-5 border-t border-white/8 flex items-center justify-end">
                        <button onClick={() => doFirstAction("skip")} disabled={finishing} data-testid="onboarding-finish"
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[var(--jr-brand)] text-white font-medium hover:bg-[var(--jr-brand-2)] disabled:opacity-60 glow-brand">
                            {finishing ? <Loader2 className="animate-spin" size={14}/> : <Check size={14}/>}
                            {t("onboarding.confirm_and_finish")}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

/* ---------------- STEP COMPONENTS ---------------- */

function WelcomeStep({ t }) {
    return (
        <div className="text-center py-6">
            <div className="w-16 h-16 rounded-2xl bg-[var(--jr-brand)]/15 grid place-items-center mx-auto">
                <Sparkles className="text-[var(--jr-brand)]" size={28}/>
            </div>
            <h2 className="font-display text-3xl tracking-tight mt-6">{t("onboarding.welcome")}</h2>
            <p className="text-[var(--jr-text-2)] mt-3 max-w-md mx-auto leading-relaxed">{t("onboarding.intro")}</p>
            <div className="mt-8 grid sm:grid-cols-3 gap-3 max-w-xl mx-auto text-left">
                <FeatChip color="var(--jr-brand)" icon={Target} label="Sales tone" desc={i18n.t("onboarding.chip_sales")}/>
                <FeatChip color="var(--jr-amber)" icon={Users} label="Recruiter tone" desc={i18n.t("onboarding.chip_recruit")}/>
                <FeatChip color="var(--jr-info)" icon={Check} label="ICP + tools" desc={i18n.t("onboarding.setup_3min")}/>
            </div>
        </div>
    );
}

function EssenceStep({ t, essence, setEssence }) {
    return (
        <div>
            <h2 className="font-display text-2xl tracking-tight">{t("onboarding.step1_title")}</h2>
            <p className="text-sm text-[var(--jr-text-2)] mt-1">{t("onboarding.step1_subtitle")}</p>
            <div className="mt-6 space-y-4">
                <Field label={t("onboarding.values")} hint={t("onboarding.values_hint")} v={essence.values} onChange={(e) => setEssence({...essence, values: e.target.value})} testid="essence-values" rows={4} placeholder={i18n.t("onboarding.values_ph")}/>
                <Field label={t("onboarding.culture")} hint={t("onboarding.culture_hint")} v={essence.culture} onChange={(e) => setEssence({...essence, culture: e.target.value})} testid="essence-culture" rows={3} placeholder={i18n.t("onboarding.culture_ph")}/>
                <Field label={t("onboarding.voice_desc")} hint={t("onboarding.voice_desc_hint")} v={essence.voice_description} onChange={(e) => setEssence({...essence, voice_description: e.target.value})} testid="essence-voice" rows={2} placeholder={i18n.t("onboarding.voice_ph")}/>
            </div>
        </div>
    );
}

function ICPStep({ t, icp, setIcp }) {
    return (
        <div>
            <h2 className="font-display text-2xl tracking-tight">{t("onboarding.icp_title")}</h2>
            <p className="text-sm text-[var(--jr-text-2)] mt-1">{t("onboarding.icp_subtitle")}</p>
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
                <IcpField icon={Building2} label={t("onboarding.icp_sector")} placeholder={t("onboarding.icp_sector_placeholder")}
                    value={icp.sector} onChange={(v) => setIcp({ ...icp, sector: v })} testid="icp-sector"/>
                <IcpField icon={Globe} label={t("onboarding.icp_country")} placeholder={t("onboarding.icp_country_placeholder")}
                    value={icp.country} onChange={(v) => setIcp({ ...icp, country: v })} testid="icp-country"/>
                <IcpField icon={Briefcase} label={t("onboarding.icp_size")} placeholder={t("onboarding.icp_size_placeholder")}
                    value={icp.size} onChange={(v) => setIcp({ ...icp, size: v })} testid="icp-size"/>
                <IcpField icon={UserIcon} label={t("onboarding.icp_role")} placeholder={t("onboarding.icp_role_placeholder")}
                    value={icp.role} onChange={(v) => setIcp({ ...icp, role: v })} testid="icp-role"/>
            </div>
            <div className="mt-5 rounded-lg border border-[var(--jr-brand)]/25 bg-[var(--jr-brand)]/6 px-4 py-3 flex items-start gap-3">
                <Sparkles className="text-[var(--jr-brand)] shrink-0 mt-0.5" size={14}/>
                <p className="text-xs text-[var(--jr-text-2)] leading-relaxed">{t("onboarding.icp_hint")}</p>
            </div>
        </div>
    );
}

function IcpField({ icon: Icon, label, placeholder, value, onChange, testid }) {
    return (
        <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--jr-text-3)] flex items-center gap-1.5">
                <Icon size={12}/> {label}
            </span>
            <input type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
                className="mt-2 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-[var(--jr-brand)] outline-none"/>
        </label>
    );
}

function IntegrationsStep({ t, status, onChange }) {
    const integ = status?.integrations || {};
    return (
        <div>
            <h2 className="font-display text-2xl tracking-tight">{t("onboarding.integrations_title")}</h2>
            <p className="text-sm text-[var(--jr-text-2)] mt-1">{t("onboarding.integrations_subtitle")}</p>
            <div className="mt-6 grid sm:grid-cols-2 gap-3">
                {INTEGRATION_META.map((m) => (
                    <IntegrationTile key={m.id} t={t} meta={m} state={integ[m.id]} onChange={onChange}/>
                ))}
            </div>
            <div className="mt-5 text-center">
                <a href="/app/integrations" target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs text-[var(--jr-text-2)] hover:text-white">
                    {t("onboarding.integrations_manage")} <ExternalLink size={11}/>
                </a>
            </div>
        </div>
    );
}

function IntegrationTile({ t, meta, state, onChange }) {
    const Icon = meta.icon;
    const configured = state?.configured;
    const [expanded, setExpanded] = useState(false);
    const [keyInput, setKeyInput] = useState("");
    const [saving, setSaving] = useState(false);

    const saveKey = async () => {
        if (!keyInput.trim()) return;
        setSaving(true);
        try {
            await api.patch(`/integrations/${meta.id}`, { api_key: keyInput.trim() });
            toast.success(i18n.t("onboarding.connected", { label: meta.label }));
            setKeyInput(""); setExpanded(false);
            onChange && onChange();
        } catch (e) {
            toast.error(e?.response?.data?.detail || i18n.t("companies.save_failed"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="rounded-xl border border-white/10 bg-[var(--jr-surface-2)] p-4" data-testid={`integ-tile-${meta.id}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg grid place-items-center" style={{ background: `${meta.color}18`, color: meta.color }}>
                        <Icon size={16}/>
                    </div>
                    <div>
                        <div className="text-sm font-medium text-white">{meta.label}</div>
                        <div className="text-[11px] text-[var(--jr-text-2)] leading-snug mt-0.5">{t(`onboarding.integrations_${meta.id}_desc`)}</div>
                    </div>
                </div>
                {configured ? (
                    <span data-testid={`integ-badge-${meta.id}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-mono uppercase tracking-[0.14em] bg-[var(--jr-success)]/15 text-[var(--jr-success)]">
                        <CheckCircle2 size={10}/> {t("onboarding.integrations_configured")}
                    </span>
                ) : (
                    <span data-testid={`integ-badge-${meta.id}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-mono uppercase tracking-[0.14em] bg-white/6 text-[var(--jr-text-3)]">
                        <AlertCircle size={10}/> {t("onboarding.integrations_not_configured")}
                    </span>
                )}
            </div>
            {configured && state?.masked && (
                <div className="mt-3 text-[11px] font-mono text-[var(--jr-text-3)]">key · {state.masked}</div>
            )}
            {!configured && !expanded && (
                <button onClick={() => setExpanded(true)} data-testid={`integ-open-${meta.id}`}
                    className="mt-3 text-xs text-[var(--jr-brand)] hover:text-[var(--jr-brand-2)]">
                    {t("onboarding.integrations_paste_key")} →
                </button>
            )}
            {!configured && expanded && (
                <div className="mt-3 flex items-center gap-2">
                    <input type="password" value={keyInput} onChange={(e) => setKeyInput(e.target.value)} placeholder="sk-… / pplx-… / …"
                        data-testid={`integ-input-${meta.id}`}
                        className="flex-1 bg-[var(--jr-surface)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[var(--jr-brand)] outline-none"/>
                    <button onClick={saveKey} disabled={saving} data-testid={`integ-save-${meta.id}`}
                        className="px-3 py-2 rounded-lg bg-[var(--jr-brand)] text-white text-xs hover:bg-[var(--jr-brand-2)] disabled:opacity-60">
                        {saving ? <Loader2 className="animate-spin" size={12}/> : t("onboarding.integrations_save")}
                    </button>
                </div>
            )}
        </div>
    );
}

function FirstActionStep({ t, onAction, disabled, previews, onGenerate, generating }) {
    return (
        <div>
            <h2 className="font-display text-2xl tracking-tight">{t("onboarding.first_action_title")}</h2>
            <p className="text-sm text-[var(--jr-text-2)] mt-1">{t("onboarding.first_action_subtitle")}</p>

            {/* Optional live preview generator */}
            <div className="mt-5 grid md:grid-cols-2 gap-3">
                <PreviewBox type="sales" label="Sales Master" accent="var(--jr-brand)" loading={generating === "sales"} preview={previews.sales} onGenerate={() => onGenerate("sales")}/>
                <PreviewBox type="recruiter" label="Recruiter Master" accent="var(--jr-amber)" loading={generating === "recruiter"} preview={previews.recruiter} onGenerate={() => onGenerate("recruiter")}/>
            </div>

            <div className="mt-6 grid sm:grid-cols-3 gap-3">
                <ActionCard testid="first-action-hunt" accent="var(--jr-brand)" icon={Radar}
                    title={t("onboarding.first_action_hunt_title")} body={t("onboarding.first_action_hunt_body")}
                    cta={t("onboarding.first_action_hunt_cta")} onClick={() => onAction("test_hunt")} disabled={disabled}/>
                <ActionCard testid="first-action-cronos" accent="var(--jr-amber)" icon={Upload}
                    title={t("onboarding.first_action_cronos_title")} body={t("onboarding.first_action_cronos_body")}
                    cta={t("onboarding.first_action_cronos_cta")} onClick={() => onAction("upload_cronos")} disabled={disabled}/>
                <ActionCard testid="first-action-skip" accent="var(--jr-text-3)" icon={ArrowRight}
                    title={t("onboarding.first_action_skip_title")} body={t("onboarding.first_action_skip_body")}
                    cta={t("onboarding.first_action_skip_cta")} onClick={() => onAction("skip")} disabled={disabled}/>
            </div>
            <p className="mt-4 text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)]">
                Elke preview kost 2 credits.
            </p>
        </div>
    );
}

function ActionCard({ icon: Icon, title, body, cta, onClick, testid, accent, disabled }) {
    return (
        <div className="rounded-xl border border-white/10 bg-[var(--jr-surface-2)] p-4 flex flex-col">
            <div className="w-9 h-9 rounded-lg grid place-items-center" style={{ background: `${accent}20`, color: accent }}>
                <Icon size={16}/>
            </div>
            <div className="mt-3 text-sm font-semibold text-white">{title}</div>
            <div className="mt-1 text-xs text-[var(--jr-text-2)] leading-relaxed flex-1">{body}</div>
            <button onClick={onClick} data-testid={testid} disabled={disabled}
                className="mt-4 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium hover:opacity-90 disabled:opacity-60"
                style={{ background: accent, color: accent === "var(--jr-text-3)" ? "#000" : "#fff" }}>
                {cta} <ArrowRight size={12}/>
            </button>
        </div>
    );
}

function ReadyStep({ t, status }) {
    const steps = status?.steps || {};
    const list = [
        { k: "essence", label: t("setup.step_essence") },
        { k: "sales_tone", label: t("setup.step_sales_tone") },
        { k: "recruiter_tone", label: t("setup.step_recruiter_tone") },
        { k: "icp", label: t("setup.step_icp") },
        { k: "integrations", label: t("setup.step_integrations") },
        { k: "first_action", label: t("setup.step_first_action") },
    ];
    return (
        <div className="text-center py-6">
            <div className="w-16 h-16 rounded-2xl bg-[var(--jr-success)]/15 grid place-items-center mx-auto">
                <Check className="text-[var(--jr-success)]" size={28}/>
            </div>
            <h2 className="font-display text-3xl tracking-tight mt-6">{t("onboarding.ready_title")}</h2>
            <p className="text-[var(--jr-text-2)] mt-3 max-w-md mx-auto leading-relaxed">{t("onboarding.ready_subtitle")}</p>
            <div className="mt-6 max-w-md mx-auto grid grid-cols-2 gap-2 text-left">
                {list.map((s) => (
                    <div key={s.k} className="flex items-center gap-2 rounded-lg border border-white/8 bg-[var(--jr-surface-2)] px-3 py-2 text-xs">
                        {steps[s.k] ? <CheckCircle2 size={13} className="text-[var(--jr-success)]"/> : <AlertCircle size={13} className="text-[var(--jr-text-3)]"/>}
                        <span className={steps[s.k] ? "text-white" : "text-[var(--jr-text-3)]"}>{s.label}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ---------------- SHARED UI ---------------- */
function ToneStep({ title, subtitle, accent, type, value, onChange }) {
    return (
        <div>
            <h2 className="font-display text-2xl tracking-tight" style={{ color: accent }}>{title}</h2>
            <p className="text-sm text-[var(--jr-text-2)] mt-1">{subtitle}</p>
            <div className="mt-6 grid sm:grid-cols-3 gap-3">
                <Slider label="Formaliteit" min={1} max={4} value={value.formality} onChange={(v) => onChange({...value, formality: v})} labels={STYLE_LABELS.formality} accent={accent} testid={`slider-${type}-formality`}/>
                <Slider label="Energie" min={1} max={4} value={value.energy} onChange={(v) => onChange({...value, energy: v})} labels={STYLE_LABELS.energy} accent={accent} testid={`slider-${type}-energy`}/>
                <Slider label="Warmte" min={1} max={4} value={value.warmth} onChange={(v) => onChange({...value, warmth: v})} labels={STYLE_LABELS.warmth} accent={accent} testid={`slider-${type}-warmth`}/>
            </div>
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
                <Field label={i18n.t("onboarding.lbl_greeting")} v={value.default_greeting} onChange={(e) => onChange({...value, default_greeting: e.target.value})} testid={`${type}-greeting`} rows={1}/>
                <Field label="Sign-off" v={value.default_signoff} onChange={(e) => onChange({...value, default_signoff: e.target.value})} testid={`${type}-signoff`} rows={1}/>
            </div>
            <Field label={i18n.t("onboarding.lbl_forbidden")} hint={i18n.t("onboarding.one_per_line")} v={value.forbidden} onChange={(e) => onChange({...value, forbidden: e.target.value})} testid={`${type}-forbidden`} rows={2}/>
            <Field label={i18n.t("onboarding.lbl_samples")} hint={i18n.t("onboarding.samples_hint")} v={value.samples} onChange={(e) => onChange({...value, samples: e.target.value})} testid={`${type}-samples`} rows={5}/>
        </div>
    );
}

function Slider({ label, min, max, value, onChange, labels, accent, testid }) {
    const idx = Math.min(max - 1, Math.max(0, value - min));
    return (
        <div>
            <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{label}</span><span className="text-xs font-medium" style={{ color: accent }}>{labels[idx]}</span></div>
            <input type="range" min={min} max={max} value={value} onChange={(e) => onChange(Number(e.target.value))} data-testid={testid} className="mt-2 w-full" style={{ accentColor: accent }}/>
            <div className="mt-1 flex justify-between text-[9px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)]"><span>{labels[0]}</span><span>{labels[3]}</span></div>
        </div>
    );
}

function Field({ label, hint, v, onChange, testid, rows = 3, placeholder }) {
    return (
        <div>
            <label className="text-xs uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{label}</label>
            <textarea value={v} onChange={onChange} rows={rows} data-testid={testid} placeholder={placeholder} className="mt-2 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[var(--jr-brand)] outline-none"/>
            {hint && <p className="mt-1 text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)]">{hint}</p>}
        </div>
    );
}

function FeatChip({ icon: Icon, label, desc, color }) {
    return (
        <div className="rounded-xl border border-white/10 bg-[var(--jr-surface-2)] p-4">
            <Icon size={16} style={{ color }}/>
            <div className="text-sm font-medium text-white mt-2">{label}</div>
            <div className="text-xs text-[var(--jr-text-2)]">{desc}</div>
        </div>
    );
}

function PreviewBox({ type, label, accent, loading, preview, onGenerate }) {
    return (
        <div className="rounded-xl border border-white/10 bg-[var(--jr-surface-2)] p-5" data-testid={`preview-${type}`}>
            <div className="flex items-center justify-between">
                <span className="text-[10px] font-mono uppercase tracking-[0.18em]" style={{ color: accent }}>{label}</span>
                <button onClick={onGenerate} disabled={loading} data-testid={`generate-${type}`}
                    className="text-xs inline-flex items-center gap-1.5 text-[var(--jr-brand)] hover:text-[var(--jr-brand-2)] disabled:opacity-60">
                    {loading ? <Loader2 className="animate-spin" size={12}/> : <Sparkles size={12}/>} {preview ? "Opnieuw" : "Genereer"}
                </button>
            </div>
            <div className="mt-3 min-h-[140px] text-sm text-white whitespace-pre-wrap leading-relaxed">
                {loading ? <div className="text-[var(--jr-text-3)]">{i18n.t("onboarding.thinking")}</div> : preview ? preview : <div className="text-[var(--jr-text-3)] italic">{i18n.t("onboarding.click_generate")}</div>}
            </div>
        </div>
    );
}
